import { ActionButton, makeStyles } from '@paintscout/ui'
import type { Action, MuiTheme as Theme } from '@paintscout/ui'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import React from 'react'
import { ItemColumn } from '../ItemTable'

export interface ItemRowContentProps {
  item: RenderableItem
  section: QuoteItemSection
  isEditable: boolean
  showAmount: boolean
  consumer?: 'crew' | 'customer'
  /** Shrinks text to the Work Order's smaller type scale (matches WorkOrderCardNext). */
  compact?: boolean
  /** Kebab actions; when omitted (or empty) the kebab is hidden. */
  actions?: Action[]
  onAction?: (action: string, item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    // ItemColumn (title/description + right-aligned price) fills the row; the kebab sits at the far
    // right, top-aligned with the title line.
    root: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(2)
    },
    itemColumn: {
      flex: 1,
      minWidth: 0
    },
    actionColumn: {
      flexShrink: 0,
      marginTop: -8,
      '& svg': {
        color: theme.paintscout.palette.brand.Neutral.P60
      },
      '@media print': {
        display: 'none'
      }
    }
  }),
  { name: 'ItemRowContent' }
)

/**
 * Shared row content for every outlined item table (Item / Options / Additional / Pending / …) in
 * the new (estimateNext) pipeline: the reused `ItemColumn` plus a top-aligned, subdued kebab. Keeps
 * the row layout identical across sections so they read as one system.
 */
export default function ItemRowContent({
  item,
  section,
  isEditable,
  showAmount,
  consumer = 'customer',
  compact,
  actions,
  onAction
}: ItemRowContentProps) {
  const classes = useStyles()
  return (
    <div className={classes.root}>
      <div className={classes.itemColumn}>
        <ItemColumn
          item={item}
          section={section}
          isEditable={isEditable}
          showAmount={showAmount}
          consumer={consumer}
          compact={compact}
        />
      </div>
      {actions && actions.length > 0 && onAction && (
        <div className={`${classes.actionColumn} no-drag`}>
          <ActionButton nextMenu actions={actions} onActionClick={(_ev, action) => onAction(action, item)} />
        </div>
      )}
    </div>
  )
}
